/**
 * Work done outside the Warehouse (Aziz, 2026-09-28): an owner or manager moves the
 * creative to the step it has really reached. The earlier steps close as "done
 * outside", with the reason on record; nobody is chased for them.
 */
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { X } from 'lucide-react';
import Button from '@/components/ui/Button';
import { cn } from '@/lib/cn';
import { doneOutside, type Creative } from '@/lib/queries';

const STEPS: Array<{ v: string; label: string; hint: string }> = [
  { v: 'shoot', label: 'Shoot', hint: 'The script is agreed; the shoot is next' },
  { v: 'raws_review', label: 'Raws review', hint: 'It was filmed; the art director checks the raws next' },
  { v: 'edit', label: 'Edit', hint: 'The raws are fine; the edit is next' },
  { v: 'dual_review', label: 'Final review', hint: 'It is edited; the final review is next' },
  { v: 'done', label: 'Done', hint: 'Finished and approved outside' },
  { v: 'posted', label: 'Posted', hint: 'Already published' },
];
const ORDER = ['brief', 'shoot', 'raws_review', 'edit', 'dual_review', 'done', 'posted'];

export default function DoneOutsideDialog({ creative, onClose }: { creative: Creative; onClose: () => void }) {
  const qc = useQueryClient();
  const here = Math.max(0, ORDER.indexOf(creative.current_stage));
  const later = STEPS.filter((s) => ORDER.indexOf(s.v) > here);
  const [reached, setReached] = useState(later[0]?.v ?? '');
  const [note, setNote] = useState('');
  const save = useMutation({
    mutationFn: () => doneOutside(creative.id, reached, note.trim()),
    onSuccess: () => {
      toast.success('Moved on. The earlier steps are closed as done outside the Warehouse.');
      qc.invalidateQueries({ queryKey: ['creatives'] });
      qc.invalidateQueries({ queryKey: ['track', creative.id] });
      qc.invalidateQueries({ queryKey: ['tasks'] });
      onClose();
    },
    onError: (e: unknown) => toast.error((e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'That did not go through'),
  });

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-end sm:items-center justify-center p-0 sm:p-4" role="dialog" aria-modal="true" data-testid="done-outside-dialog">
      <div className="w-full sm:max-w-lg bg-bg-card border border-slate-800 rounded-t-2xl sm:rounded-2xl p-5 max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between gap-3 mb-1">
          <h2 className="text-base font-semibold text-muted-strong">Done outside the Warehouse</h2>
          <button type="button" onClick={onClose} className="p-1 text-muted hover:text-muted-strong" aria-label="Close"><X size={16} /></button>
        </div>
        <p className="text-sm text-muted mb-4">
          {creative.code} is at <span className="text-muted-strong">{ORDER[here] === 'brief' ? 'Brief' : STEPS.find((s) => s.v === ORDER[here])?.label ?? creative.current_stage}</span>.
          Pick the step it has really reached. The steps before it close as done outside: their open tasks close with your note and nobody is chased for them.
        </p>
        {later.length === 0 ? <p className="text-sm text-muted">It is already at the last step.</p> : (
          <div className="grid gap-2 mb-4" role="radiogroup" aria-label="The step it has reached">
            {later.map((s) => (
              <button key={s.v} type="button" role="radio" aria-checked={reached === s.v} onClick={() => setReached(s.v)} data-testid={`reached-${s.v}`}
                className={cn('text-left rounded-lg border px-3 py-2', reached === s.v ? 'border-accent-400 bg-accent-600/10' : 'border-slate-800 hover:border-slate-700')}>
                <div className="text-sm text-muted-strong">{s.label}</div>
                <div className="text-xs text-muted">{s.hint}</div>
              </button>
            ))}
          </div>
        )}
        <label className="block text-xs text-muted mb-1" htmlFor="done-outside-note">What was done outside, and where</label>
        <textarea id="done-outside-note" value={note} onChange={(e) => setNote(e.target.value)} rows={3} maxLength={1000} dir="auto" data-testid="done-outside-note"
          placeholder="e.g. Filmed and edited by the clinic's own team; final approved by Dr. Ahmed on WhatsApp, 20 Sep."
          className="w-full px-3 py-2 rounded-lg bg-bg border border-slate-800 text-sm text-muted-strong" />
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={() => save.mutate()} loading={save.isPending} disabled={!reached || note.trim().length < 3} data-testid="done-outside-save">
            Move to {STEPS.find((s) => s.v === reached)?.label ?? '...'}
          </Button>
        </div>
      </div>
    </div>
  );
}
